import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCommunity } from '@/contexts/CommunityContext';
import { supabase } from '@/integrations/supabase/client';
import { CommunityPageWithSections, CommunityPageSectionRow } from '@/types/cms';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { Loader2, Eye, Save, Settings, FileText, Smartphone, Monitor, Tablet } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { SectionRenderer } from '@/components/cms';
import { 
  HeroSectionEditor, 
  TextBlockEditor, 
  GridSectionEditor, 
  VideoSectionEditor, 
  CTASectionEditor 
} from '@/components/admin/cms';
import SectionList from '@/components/admin/cms/SectionList';
import { useSectionReorder } from '@/hooks/useSectionReorder';

const ContentManagement = () => {
  const { currentCommunity } = useCommunity();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  
  const [selectedPageType, setSelectedPageType] = useState<string>('about');
  const [previewMode, setPreviewMode] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [editingSection, setEditingSection] = useState<string | null>(null);
  
  // Section reordering hook
  const sectionReorderMutation = useSectionReorder(currentCommunity?.id, selectedPageType);

  // Fetch community pages
  const { data: pageData, isLoading, error } = useQuery({
    queryKey: ['admin-community-pages', currentCommunity?.id, selectedPageType],
    queryFn: async () => {
      if (!currentCommunity?.id) throw new Error('No community selected');

      const { data, error } = await supabase
        .from('community_pages')
        .select(`
          *,
          sections:community_page_sections(*)
        `)
        .eq('community_id', currentCommunity.id)
        .eq('page_type', selectedPageType)
        .single();

      if (error) {
        console.error('Error fetching community page:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Page not found');
      }

      // Sort sections by display_order
      const sortedSections = (data.sections || [])
        .sort((a, b) => a.display_order - b.display_order);

      return {
        ...data,
        sections: sortedSections
      } as CommunityPageWithSections;
    },
    enabled: !!currentCommunity?.id,
  });

  // Update section mutation
  const updateSectionMutation = useMutation({
    mutationFn: async ({ sectionId, updates }: { sectionId: string; updates: Partial<CommunityPageSectionRow> }) => {
      const { error } = await supabase
        .from('community_page_sections')
        .update(updates)
        .eq('id', sectionId);

      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['admin-community-pages', currentCommunity?.id, selectedPageType] 
      });
      toast({
        title: "Section updated",
        description: "The section has been updated successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update section.",
        variant: 'destructive',
      });
      console.error('Error updating section:', error);
    }
  });

  const getPreviewWidthClass = () => {
    switch (previewMode) {
      case 'mobile': return 'w-[375px]'; // iPhone standard width
      case 'tablet': return 'w-[768px]'; // iPad standard width
      default: return 'w-full'; // Full panel width for desktop
    }
  };

  const renderSectionEditor = (section: CommunityPageSectionRow) => {
    const commonProps = {
      section,
      onSave: (updates: Partial<CommunityPageSectionRow>) => {
        updateSectionMutation.mutate({ sectionId: section.id, updates });
        setEditingSection(null);
      },
      onCancel: () => setEditingSection(null),
      isLoading: updateSectionMutation.isPending
    };

    switch (section.section_type) {
      case 'hero':
        return <HeroSectionEditor {...commonProps} />;
      case 'text_block':
        return <TextBlockEditor {...commonProps} />;
      case 'grid':
        return <GridSectionEditor {...commonProps} />;
      case 'video':
        return <VideoSectionEditor {...commonProps} />;
      case 'cta':
        return <CTASectionEditor {...commonProps} />;
      default:
        return (
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-muted-foreground">
              Editor for {section.section_type} sections is not yet implemented.
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setEditingSection(null)}>
                Close
              </Button>
            </div>
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="flex space-x-2">
          <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse"></div>
          <div className="h-3 w-3 bg-gradient-to-r from-accent to-primary rounded-full animate-pulse animation-delay-200"></div>
          <div className="h-3 w-3 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse animation-delay-400"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold mb-2">Error Loading Page</h2>
        <p className="text-muted-foreground">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in">
      {/* Header */}
      <div className="relative">
        <h1 className="admin-section-title mb-2">Content Management</h1>
        <p className="text-muted-foreground/80 text-lg">
          Edit and manage your community pages with real-time preview
        </p>
      </div>
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Page Editor</h2>
          <p className="text-sm text-muted-foreground/80">Manage sections and preview your content</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Select value={selectedPageType} onValueChange={setSelectedPageType}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="Select page" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="about">About Page</SelectItem>
              <SelectItem value="terms">Terms of Service</SelectItem>
              <SelectItem value="privacy">Privacy Policy</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Main Content - Adaptive Layout */}
      {isMobile ? (
        // Mobile: Stacked layout with tabs
        <div className="space-y-6">
          {/* Mobile Tab Navigation */}
          <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
            <button
              onClick={() => setEditingSection(editingSection ? null : 'sections')}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                editingSection ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-600'
              }`}
            >
              <Settings className="h-4 w-4 mx-auto mb-1" />
              Edit Sections
            </button>
            <button
              onClick={() => setEditingSection(null)}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                !editingSection ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-600'
              }`}
            >
              <Eye className="h-4 w-4 mx-auto mb-1" />
              Preview
            </button>
          </div>
          
          {editingSection ? (
            // Mobile Section Editor
            <Card className="admin-card admin-card-stats">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Page Sections
                </CardTitle>
                <CardDescription className="text-muted-foreground/80">
                  Edit individual sections of your page
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {pageData?.sections && pageData.sections.length > 0 ? (
                  <SectionList
                    sections={pageData.sections}
                    onSectionReorder={(reorderedSections) => {
                      sectionReorderMutation.mutate(reorderedSections);
                    }}
                    editingSection={editingSection}
                    onEditSection={setEditingSection}
                    renderSectionEditor={renderSectionEditor}
                  />
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground/80">No sections found for this page</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            // Mobile Preview
            <Card className="admin-card admin-card-stats">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                      <Eye className="h-5 w-5" />
                      Live Preview
                    </CardTitle>
                    <CardDescription className="text-muted-foreground/80">
                      See how your page looks to visitors
                    </CardDescription>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Select value={previewMode} onValueChange={(value: 'desktop' | 'tablet' | 'mobile') => setPreviewMode(value)}>
                      <SelectTrigger className="w-full sm:w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="desktop">
                          <div className="flex items-center gap-2">
                            <Monitor className="h-4 w-4" />
                            Desktop
                          </div>
                        </SelectItem>
                        <SelectItem value="tablet">
                          <div className="flex items-center gap-2">
                            <Tablet className="h-4 w-4" />
                            Tablet
                          </div>
                        </SelectItem>
                        <SelectItem value="mobile">
                          <div className="flex items-center gap-2">
                            <Smartphone className="h-4 w-4" />
                            Mobile
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className={`border rounded-lg bg-white min-h-96 overflow-y-auto transition-all duration-300 mx-auto ${getPreviewWidthClass()}`}>
                  {pageData?.sections?.map((section) => (
                    <div key={section.id} className="relative">
                      <SectionRenderer section={section} isPreview={true} />
                    </div>
                  ))}
                  
                  {(!pageData?.sections || pageData.sections.length === 0) && (
                    <div className="flex items-center justify-center h-96 text-muted-foreground">
                      <div className="text-center">
                        <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No sections configured</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        // Desktop: Resizable panels layout
        <div className="h-[calc(100vh-16rem)]">
          <PanelGroup direction="horizontal" className="h-full">
            {/* Left Panel - Section Editor */}
            <Panel defaultSize={50} minSize={30}>
              <Card className="admin-card admin-card-stats h-full flex flex-col">
                <CardHeader className="flex-shrink-0">
                  <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Page Sections
                  </CardTitle>
                  <CardDescription className="text-muted-foreground/80">
                    Edit individual sections of your page. Changes will appear in the preview panel.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto p-6 space-y-4">
                  {pageData?.sections && pageData.sections.length > 0 ? (
                    <SectionList
                      sections={pageData.sections}
                      onSectionReorder={(reorderedSections) => {
                        sectionReorderMutation.mutate(reorderedSections);
                      }}
                      editingSection={editingSection}
                      onEditSection={setEditingSection}
                      renderSectionEditor={renderSectionEditor}
                    />
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground/80">No sections found for this page</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </Panel>

            {/* Resize Handle */}
            <PanelResizeHandle className="w-2 bg-border hover:bg-accent-foreground/20 transition-all duration-300 rounded-sm flex items-center justify-center group">
              <div className="w-1 h-8 bg-border group-hover:bg-accent-foreground/40 rounded-full transition-all duration-300"></div>
            </PanelResizeHandle>

            {/* Right Panel - Live Preview */}
            <Panel defaultSize={50} minSize={30}>
              <Card className="admin-card admin-card-stats h-full flex flex-col">
                <CardHeader className="flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                        <Eye className="h-5 w-5" />
                        Live Preview
                      </CardTitle>
                      <CardDescription className="text-muted-foreground/80">
                        See how your page looks to visitors
                      </CardDescription>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-muted-foreground hidden sm:block">
                        Device preview
                      </div>
                      <Select value={previewMode} onValueChange={(value: 'desktop' | 'tablet' | 'mobile') => setPreviewMode(value)}>
                        <SelectTrigger className="w-36 transition-all duration-200 hover:border-primary/50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="desktop">
                            <div className="flex items-center gap-2">
                              <Monitor className="h-4 w-4" />
                              Desktop
                            </div>
                          </SelectItem>
                          <SelectItem value="tablet">
                            <div className="flex items-center gap-2">
                              <Tablet className="h-4 w-4" />
                              Tablet
                            </div>
                          </SelectItem>
                          <SelectItem value="mobile">
                            <div className="flex items-center gap-2">
                              <Smartphone className="h-4 w-4" />
                              Mobile
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 overflow-hidden p-6 flex justify-center">
                  <div className={`border rounded-lg bg-white h-full overflow-y-auto transition-all duration-300 @container ${getPreviewWidthClass()}`}>
                      {pageData?.sections?.map((section) => (
                        <div key={section.id} className="relative">
                          <SectionRenderer section={section} isPreview={true} />
                        </div>
                      ))}
                      
                      {(!pageData?.sections || pageData.sections.length === 0) && (
                        <div className="flex items-center justify-center h-96 text-muted-foreground">
                          <div className="text-center">
                            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                            <p>No sections configured</p>
                          </div>
                        </div>
                      )}
                    </div>
                </CardContent>
              </Card>
            </Panel>
          </PanelGroup>
        </div>
      )}
    </div>
  );
};

export default ContentManagement;